import React from "react";
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { useThemeMode } from "../../theme/ThemeModeContext";
import { THEME_MODES } from "../../theme/themeConfig";
import { SIDEBAR_WIDTH_COLLAPSED } from "./sidebarConstants";

const fontSans = '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const iconMap = {
  light: LightModeOutlinedIcon,
  dark: DarkModeOutlinedIcon,
};

export default function SidebarThemeSwitcher({ collapsed }) {
  const { mode, setMode } = useThemeMode();
  const theme = useTheme();
  const s = theme.admin?.sidebar || {};

  const toggleSx = {
    width: "100%",
    bgcolor: "rgba(0,0,0,0.2)",
    borderRadius: 2.5,
    p: 0.35,
    border: `1px solid ${s.footerBorder}`,
    "& .MuiToggleButtonGroup-grouped": {
      border: 0,
      borderRadius: "10px !important",
      mx: 0.15,
    },
    "& .MuiToggleButton-root": {
      flex: 1,
      border: "none",
      color: s.itemText,
      fontSize: 11,
      fontWeight: 600,
      textTransform: "none",
      py: 0.75,
      px: 0.5,
      gap: 0.4,
      transition: "all 0.2s ease",
      "&.Mui-selected": {
        bgcolor: "rgba(212,168,74,0.28)",
        color: s.itemTextActive,
        boxShadow: "0 2px 12px rgba(212,168,74,0.25)",
      },
      "&:hover": { bgcolor: s.hoverBg },
    },
  };

  const collapsedBtnSize = 36;
  const collapsedTrackWidth = Math.min(collapsedBtnSize + 8, SIDEBAR_WIDTH_COLLAPSED - 16);

  if (collapsed) {
    return (
      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          maxWidth: SIDEBAR_WIDTH_COLLAPSED,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 0.5,
          pb: 1.5,
          pt: 0.5,
          zIndex: 1,
          boxSizing: "border-box",
        }}
      >
        <Stack
          spacing={0.35}
          alignItems="center"
          sx={{
            width: collapsedTrackWidth,
            maxWidth: "100%",
            p: 0.35,
            borderRadius: 2,
            bgcolor: "rgba(0,0,0,0.22)",
            border: `1px solid ${s.footerBorder}`,
          }}
        >
          {THEME_MODES.map((t) => {
            const Icon = iconMap[t.id];
            const active = mode === t.id;
            return (
              <Tooltip key={t.id} title={t.label} placement="right" arrow>
                <IconButton
                  size="small"
                  aria-label={t.label}
                  aria-pressed={active}
                  onClick={() => setMode(t.id)}
                  sx={{
                    width: collapsedBtnSize,
                    height: collapsedBtnSize - 2,
                    borderRadius: 1.5,
                    color: active ? s.itemIconActive : s.itemIcon,
                    bgcolor: active ? "rgba(212,168,74,0.28)" : "transparent",
                    border: active ? `1px solid ${s.accentBorder}` : "1px solid transparent",
                    transition: "all 0.18s ease",
                    "&:hover": {
                      bgcolor: active ? "rgba(212,168,74,0.34)" : s.hoverBg,
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            );
          })}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        mx: 1.25,
        mb: 1.75,
        p: 1.35,
        borderRadius: 3,
        position: "relative",
        zIndex: 1,
        bgcolor: s.footerBg,
        border: `1px solid ${s.footerBorder}`,
        backdropFilter: "blur(12px)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <PaletteOutlinedIcon sx={{ fontSize: 14, color: s.goldMuted, opacity: 0.9 }} />
        <Typography
          sx={{
            fontSize: 10,
            color: s.footerMuted,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontFamily: fontSans,
          }}
        >
          Appearance
        </Typography>
      </Stack>
      <ToggleButtonGroup exclusive fullWidth value={mode} onChange={(_, v) => v && setMode(v)} size="small" sx={toggleSx}>
        {THEME_MODES.map((t) => {
          const Icon = iconMap[t.id];
          return (
            <ToggleButton key={t.id} value={t.id}>
              <Stack direction="row" spacing={0.4} alignItems="center" justifyContent="center">
                <Icon sx={{ fontSize: 15 }} />
                <span>{t.label}</span>
              </Stack>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
}
